import React, { useState, useContext, useRef } from "react";
import Semester, { EmptySemester, EmptySemesters } from "../types/Semester";
import { UserContext } from "./UserContext";
import Student, { EmptyStudent } from "../types/Student";
import axios from "axios";
import { UniversityContext } from "./UniversityContext";
import { convertToSelect } from "../hooks/useScript";

type Props = {
  children: React.ReactChild;
};
type SemesterContextType = {
  semesters: Array<Semester>;
  selectedSemester: Semester;
  selectedStudent: Student;
  selectedSemesterVersion?: number;
  setSelectedSemesterVersion?: any;
  [key: string]: any;
};

export const SemesterContext = React.createContext<SemesterContextType>({
  semesters: EmptySemesters,
  selectedSemester: EmptySemester,
  selectedStudent: EmptyStudent,
  selectedSemesterVersion: 0,
  semesterVersionsLoading: false,
  setAllSemester: (semester: Array<Semester>) => { },
  setSemester: (semester: Array<Semester>) => { },
  setSelectedSemesterVersion: (version: number) => { },
  loadSemestersForProgram: () => { },
  clearSemesterState: () => { },
});

const SemesterContextProvider = (props: Props) => {
  const [semesters, setSemesters] = useState<Array<Semester>>(EmptySemesters);
  const [selectedSemester, setSelectedSemester] =
    useState<Semester>(EmptySemester);
  const [selectedStudent, setSelectedStudent] = useState<Student>(
    JSON.parse(localStorage.getItem("selectedStudent") as string) ||
    EmptyStudent
  );
  const [semesterVersions, setSemesterVersions] = useState<any>([]);
  const [semesterVersionsLoading, setSemesterVersionsLoading] =
    useState<boolean>(false);
  const [selectedSemesterVersion, setSelectedSemesterVersion] =
    useState<number>(0);
  const userContext = useContext(UserContext);
  const universityContext: any = useContext(UniversityContext);
  const latestSemesterRequestIdRef = useRef(0);
  const latestVersionRequestIdRef = useRef(0);
  const latestDatesRequestIdRef = useRef(0);

  const isInstructorOrAdmin = [2, 6].includes(userContext.user.role);

  const DATE_TYPE_FIELD_MAP: Record<number, string> = {
    1: "add_drop_date",
    2: "final_assess_date",
    3: "competency_recording_deadline",
    4: "instructor_grading_deadline",
    5: "overload_request_deadline",
  };

  const resolveProgramShortName = (semester: Semester): string =>
    semester?.program_name ||
    universityContext?.selectedProgram?.short_name ||
    "";

  const resolveProgramId = (semester: Semester): string => {
    if (!isInstructorOrAdmin) {
      return userContext?.user?.programs?.[0]?.id || "";
    }

    const programName = resolveProgramShortName(semester);
    if (programName && universityContext?.programs?.length) {
      const match = universityContext.programs.find(
        (p: any) =>
          p.short_name === programName ||
          p.label === programName ||
          p.value === programName ||
          p.program_name === programName
      );
      if (match?.id) return match.id;
    }

    return universityContext?.selectedProgram?.id || "";
  };

  const fetchProgramSemesterDates = (semester: Semester) => {
    if (!semester?.value || semester?.label === "All") return;

    const programId = resolveProgramId(semester);

    if (!programId) return;

    const requestId = ++latestDatesRequestIdRef.current;

    axios
      .get(`/program/semester-date`, {
        params: { semester_id: semester.value, program_id: programId },
      })
      .then((res) => {
        if (requestId !== latestDatesRequestIdRef.current) return;

        const entry = res.data?.program_semester_dates?.[0];
        const programDates: any[] = entry?.program_dates || [];

        const dateFields: Record<string, string> = {};
        programDates.forEach((d: any) => {
          const field = DATE_TYPE_FIELD_MAP[d.date_type_id];
          if (field) dateFields[field] = d.date_value;
        });

        setSelectedSemester((prev) => {
          if (prev?.value !== semester.value) return prev;
          const merged = { ...prev, ...dateFields } as Semester;
          localStorage.setItem("selectedSemester", JSON.stringify(merged));
          return merged;
        });
      })
      .catch((err) => {
        if (requestId !== latestDatesRequestIdRef.current) return;
        console.error("Failed to fetch program semester dates", err);
      });
  };

  const tagSemestersWithProgram = (
    semesterOptions: Semester[],
    programShortName: string
  ): Semester[] =>
    semesterOptions.map((s) => ({
      ...s,
      program_name: programShortName,
    }));

  const clearSemesterState = () => {
    setSemesters([]);
    setSelectedSemester(EmptySemester);
    setSelectedSemesterVersion(0);
    setSemesterVersions([]);
    setSemesterVersionsLoading(false);
    localStorage.removeItem("selectedSemester");

    localStorage.removeItem("allSemesters");
  };

  const setAllSemester = (semester: any) => {
    if (!Array.isArray(semester)) {
      setSemesters([]);
      return;
    }
    if (userContext.user.role === 1) {
      const index_of_first_term = semester?.indexOf(
        semester.find((v: any) => v.label === userContext.user?.programs?.[0]?.term_entered)
      );
      const result = semester.slice(index_of_first_term);
      if (semester[0]?.label === "All")
        result.unshift({ label: "All", value: "All", year: "" });
      index_of_first_term > -1 ? setSemesters(result) : setSemesters(semester);
    } else {
      setSemesters(semester);
    }
  };

  const fetchSemesterVersions = (semester: Semester) => {
    if (semester?.label === "All") {
      setSemesterVersionsLoading(false);
      setSelectedSemesterVersion(0);
      setSemesterVersions([]);
      axios
        .get(`/version?university_code=CMKL`)
        .then((res) => {
          setSemesterVersions(
            res.data.versions.map((v: any) => ({
              curriculum_version: v.version_number,
              display_version: v.display_version,
              title: v.title,
              program_name: v.program_name,
            }))
          );
        })
        .catch((err) => {
          console.error("Failed to fetch active prime version", err);
        });
      return;
    }

    const programName = resolveProgramShortName(semester);

    if (!semester?.label || !programName) {
      setSemesterVersionsLoading(false);
      setSelectedSemesterVersion(0);
      setSemesterVersions([]);
      return;
    }

    const requestId = ++latestVersionRequestIdRef.current;
    setSemesterVersionsLoading(true);

    axios
      .get(
        `/semester/versions?university_code=CMKL&semester_name=${encodeURIComponent(
          semester.label
        )}&program_name=${encodeURIComponent(programName)}`
      )
      .then((res) => {
        if (requestId !== latestVersionRequestIdRef.current) return;
        if (res.data?.versions?.length > 0) {
          setSelectedSemesterVersion(res.data.versions[0].curriculum_version);
          setSemesterVersions(res.data.versions);
        } else {
          setSelectedSemesterVersion(0);
          setSemesterVersions([]);
        }
      })
      .catch((err) => {
        if (requestId !== latestVersionRequestIdRef.current) return;
        console.error("Failed to fetch active prime version", err);
        setSelectedSemesterVersion(0);
        setSemesterVersions([]);
      })
      .finally(() => {
        if (requestId === latestVersionRequestIdRef.current) {
          setSemesterVersionsLoading(false);
        }
      });
  };

  const setSemester = (semester: Semester) => {
    // Empty label clears selection (used on logout / before explore init).
    if (!semester?.label) {
      ++latestVersionRequestIdRef.current;
      ++latestDatesRequestIdRef.current;
      setSelectedSemester(EmptySemester);
      setSelectedSemesterVersion(0);
      setSemesterVersions([]);
      setSemesterVersionsLoading(false);
      localStorage.removeItem("selectedSemester");
      return;
    }

    const programShortName = resolveProgramShortName(semester);

    const normalized: Semester = {
      ...semester,
      program_name: programShortName,
    };

    localStorage.setItem("selectedSemester", JSON.stringify(normalized));
    fetchSemesterVersions(normalized);
    fetchProgramSemesterDates(normalized);
    setSelectedSemester(normalized);
  };

  const applyDefaultSemesterSelection = (
    semesterOptions: Semester[],
    programShortName: string,
    resetSelection: boolean,
    requestId: number
  ) => {
    const withProgram = tagSemestersWithProgram(
      semesterOptions,
      programShortName
    );

    const applyIfCurrent = (semester: Semester) => {
      if (requestId !== latestSemesterRequestIdRef.current) return;
      setSemester(semester);
    };

    if (!resetSelection) {
      const stored = localStorage.getItem("selectedSemester");
      if (stored) {
        try {
          const initial = JSON.parse(stored);
          const match = withProgram.find(
            (s) => s.label === initial.label || s.value === initial.value
          );
          if (match) {
            applyIfCurrent(match);
            return;
          }
        } catch {
          // ignore invalid localStorage
        }
      }
    }

    localStorage.removeItem("selectedSemester");

    const pickFallback = () => {
      if (requestId !== latestSemesterRequestIdRef.current) return;
      if (withProgram.length > 0) {
        applyIfCurrent(withProgram[withProgram.length - 1]);
      } else {
        clearSemesterState();
      }
    };

    axios
      .get(
        `/semester/current?university_code=CMKL&program_name=${encodeURIComponent(
          programShortName
        )}`
      )
      .then((res) => {
        if (requestId !== latestSemesterRequestIdRef.current) return;
        const currentName = res.data?.semesters?.semester_name;
        if (currentName) {
          const match = withProgram.find((s) => s.label === currentName);
          if (match) {
            applyIfCurrent(match);
            return;
          }
        }
        pickFallback();
      })
      .catch(() => {
        pickFallback();
      });
  };

  const loadSemestersForProgram = (
    programShortName: string,
    resetSelection = false
  ) => {
    if (!programShortName) {
      clearSemesterState();
      return;
    }

    const requestId = ++latestSemesterRequestIdRef.current;
    ++latestVersionRequestIdRef.current;
    setSemesterVersionsLoading(true);
    setSelectedSemesterVersion(0);
    setSemesterVersions([]);

    axios
      .get(
        `/semester?university_code=CMKL&program_name=${encodeURIComponent(
          programShortName
        )}`
      )
      .then((res) => {
        if (requestId !== latestSemesterRequestIdRef.current) return;

        const rawSemesters = res.data?.semesters;
        if (!rawSemesters || rawSemesters.length === 0) {
          clearSemesterState();
          return;
        }

        const allSemestersConvert = convertToSelect(
          rawSemesters,
          "semester_name",
          "id"
        );
        const taggedSemesters = tagSemestersWithProgram(
          allSemestersConvert,
          programShortName
        );
        setAllSemester(taggedSemesters);
        localStorage.setItem(
          "allSemesters",
          JSON.stringify(taggedSemesters)
        );
        applyDefaultSemesterSelection(
          allSemestersConvert,
          programShortName,
          resetSelection,
          requestId
        );
      })
      .catch(() => {
        if (requestId !== latestSemesterRequestIdRef.current) return;
        clearSemesterState();
      });
  };

  const setStudent = (student: Student) => {
    localStorage.setItem("selectedStudent", JSON.stringify(student));
    setSelectedStudent(student);
  };

  return (
    <SemesterContext.Provider
      value={{
        semesters,
        selectedSemester,
        semesterVersions,
        semesterVersionsLoading,
        selectedStudent,
        selectedSemesterVersion,
        setSelectedSemesterVersion,
        setStudent,
        setAllSemester,
        setSemester,
        loadSemestersForProgram,
        clearSemesterState,
      }}
    >
      {props.children}
    </SemesterContext.Provider>
  );
};

export default SemesterContextProvider;
